'use client'

import { useState } from 'react'
import { useSearchParams, useRouter, usePathname } from 'next/navigation'
import { useTranslation } from 'components/providers/TranslationProvider'
import { Card, CardContent, CardHeader, CardTitle } from 'ui/card'
import { Input } from 'ui/input'
import { Button } from 'ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'ui/table'
import { Search, Loader2, ArrowLeft } from 'lucide-react'
import LoadingAnimation from 'layout/LoadingAnimation'
import ProclubsTeamDetail from './ProclubsTeamDetail'
import type { ProclubsClubData } from 'lib/api/proclubs-public'

interface ProclubsPageClientProps {
    clubId?: string
    clubData?: ProclubsClubData | null
}

interface CachedClub {
    clubId: string
    name: string
    lastViewed?: number
}

const CACHE_KEY = 'proclubs_cached_clubs'
const MAX_CACHE_SIZE = 100

// Helper functions for localStorage cache
const getCachedClubs = (): CachedClub[] => {
    if (typeof window === 'undefined') return []
    try {
        const cached = localStorage.getItem(CACHE_KEY)
        return cached ? JSON.parse(cached) : []
    } catch {
        return []
    }
}

const saveCachedClub = (club: CachedClub) => {
    if (typeof window === 'undefined') return
    try {
        const cached = getCachedClubs()
        // Remove existing entry if present
        const filtered = cached.filter(c => c.clubId !== club.clubId)
        // Add to beginning
        filtered.unshift({ ...club, lastViewed: Date.now() })
        // Keep only MAX_CACHE_SIZE most recent
        const trimmed = filtered.slice(0, MAX_CACHE_SIZE)
        localStorage.setItem(CACHE_KEY, JSON.stringify(trimmed))
    } catch (error) {
        console.error('Failed to cache club:', error)
    }
}

/**
 * ProclubsPageClient - Client Component
 *
 * Handles:
 * - Team search functionality
 * - Displaying search results in a table
 * - Navigating to team detail view
 */
const ProclubsPageClient = ({ clubId, clubData }: ProclubsPageClientProps) => {
    const { t, isLoading: translationsLoading } = useTranslation()
    const router = useRouter()
    const pathname = usePathname()
    const searchParams = useSearchParams()
    
    const [searchQuery, setSearchQuery] = useState('')
    const [isSearching, setIsSearching] = useState(false)
    const [searchResults, setSearchResults] = useState<Array<{ clubId: string; name: string }>>([])
    const [searchError, setSearchError] = useState<string | null>(null)

    if (translationsLoading || !t) {
        return <LoadingAnimation />
    }

    // If clubId is in URL, show team detail
    if (clubId) {
        const handleBackToSearch = () => {
            const params = new URLSearchParams(searchParams.toString())
            params.delete('club_id')
            router.push(`${pathname}?${params.toString()}`)
        }

        return (
            <div className='flex flex-col gap-4'>
                <Button variant='ghost' onClick={handleBackToSearch} className='w-fit'>
                    <ArrowLeft className='mr-2 h-4 w-4' />
                    Back to Search
                </Button>
                <ProclubsTeamDetail clubId={clubId} clubData={clubData} />
            </div>
        )
    }

    const handleSearch = async () => {
        if (!searchQuery.trim()) {
            setSearchError('Please enter a club ID or team name')
            return
        }

        const query = searchQuery.trim()
        setIsSearching(true)
        setSearchError(null)
        setSearchResults([])

        try {
            const response = await fetch(`/api/proclubs/search?query=${encodeURIComponent(query)}`)
            const data = await response.json()

            if (!response.ok) {
                setSearchError(data.error || 'Search failed')
                return
            }

            // Transform API response to results format
            const clubs = data.clubs || []
            
            if (clubs.length === 0) {
                setSearchError('No clubs found')
                return
            }

            // Cache all found clubs
            clubs.forEach((club: { clubId: string; clubName: string }) => {
                saveCachedClub({
                    clubId: club.clubId,
                    name: club.clubName,
                })
            })

            // Add found clubs to results
            setSearchResults(clubs.map((club: { clubId: string; clubName: string }) => ({
                clubId: club.clubId,
                name: club.clubName,
            })))
        } catch (error) {
            setSearchError('Failed to search. Please try again.')
            console.error('Search error:', error)
        } finally {
            setIsSearching(false)
        }
    }

    const handleTeamClick = (clubId: string) => {
        const params = new URLSearchParams(searchParams.toString())
        params.set('club_id', clubId)
        router.push(`${pathname}?${params.toString()}`)
    }

    return (
        <div className='flex flex-col gap-4'>
            <Card>
                <CardHeader>
                    <CardTitle>FIFA Pro Clubs Data</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className='flex gap-2'>
                        <Input
                            placeholder='Enter Club ID (e.g., 2425) or team name'
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleSearch()
                                }
                            }}
                            className='flex-1'
                        />
                        <Button onClick={handleSearch} disabled={isSearching}>
                            {isSearching ? (
                                <Loader2 className='h-4 w-4 animate-spin' />
                            ) : (
                                <Search className='h-4 w-4' />
                            )}
                            Search
                        </Button>
                    </div>
                    <p className='text-xs text-muted-foreground mt-2'>
                        Search by Club ID (numeric) or team name. Name search uses EA's all-time leaderboard.
                    </p>
                    {searchError && (
                        <p className='text-sm text-red-500 mt-2'>{searchError}</p>
                    )}
                </CardContent>
            </Card>

            {searchResults.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle>Search Results</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Club ID</TableHead>
                                    <TableHead>Team Name</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {searchResults.map((team) => (
                                    <TableRow 
                                        key={team.clubId}
                                        className='cursor-pointer hover:bg-muted/50'
                                        onClick={() => handleTeamClick(team.clubId)}
                                    >
                                        <TableCell>{team.clubId}</TableCell>
                                        <TableCell className='font-medium'>{team.name}</TableCell>
                                        <TableCell>
                                            <Button 
                                                variant='outline' 
                                                size='sm'
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    handleTeamClick(team.clubId)
                                                }}
                                            >
                                                View Details
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
        </div>
    )
}

export default ProclubsPageClient

